import { Check, Sparkles, TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { DiffSegment } from "@/features/ai";
import type { PolishState } from "@/stores/storyWorkspaceStore";

type PolishDiffPanelProps = {
  onApply: () => void;
  onDismiss: () => void;
  onPromptAction?: () => void;
  state: PolishState;
};

function DiffText({ segments }: { segments: DiffSegment[] }) {
  return (
    <div className="whitespace-pre-wrap rounded-md border border-ink/10 bg-white p-3 text-sm leading-7 text-foreground">
      {segments.map((segment) => (
        <span
          className={cn(
            segment.kind === "added" && "bg-emerald-100 text-emerald-950",
            segment.kind === "removed" && "bg-red-100 text-red-950 line-through decoration-red-700",
          )}
          key={segment.index}
        >
          {segment.text}
        </span>
      ))}
    </div>
  );
}

export function PolishDiffPanel({
  onApply,
  onDismiss,
  onPromptAction,
  state,
}: PolishDiffPanelProps) {
  const { t } = useTranslation();
  const canApply = Boolean(state.suggestion && state.diff && !state.noImprovements);

  return (
    <section
      aria-live="polite"
      className="space-y-4 rounded-md border border-ink/10 bg-white/95 p-4 shadow-vellum"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <Sparkles className="h-4 w-4 text-clay" />
            <h3 className="font-display text-xl leading-none text-foreground">
              {t("story.ai.polish.title")}
            </h3>
            {typeof state.creditsRemaining === "number" ? (
              <Badge variant="outline">
                {t("story.ai.textEnhancement.creditsRemaining", {
                  count: state.creditsRemaining,
                })}
              </Badge>
            ) : null}
          </div>
          {state.sourceContext ? (
            <p className="text-xs leading-5 text-muted-foreground">
              {t("story.ai.polish.sourceContext", {
                chapter: state.sourceContext.chapterTitle,
                included: state.sourceContext.includedWordCount ?? 0,
                language: state.sourceContext.language ?? t("story.ai.textEnhancement.unspecified"),
                original: state.sourceContext.originalWordCount ?? 0,
              })}
            </p>
          ) : null}
        </div>
        <Button onClick={onDismiss} size="sm" type="button" variant="ghost">
          {t("story.ai.textEnhancement.dismiss")}
        </Button>
      </div>

      {state.status === "loading" ? (
        <p className="text-sm text-muted-foreground">
          {t("story.ai.polish.loading")}
        </p>
      ) : null}

      {state.status === "error" ? (
        <div className="space-y-3 rounded-md border border-destructive/20 bg-destructive/10 p-3 text-sm text-destructive">
          <p className="flex items-center gap-2 font-medium">
            <TriangleAlert className="h-4 w-4" />
            {state.error ? t(state.error) : t("story.ai.polish.errors.failed")}
          </p>
          {state.prompt ? (
            <Button onClick={onPromptAction} size="sm" type="button" variant="outline">
              {t(state.prompt.ctaLabelKey ?? "story.ai.textEnhancement.dismiss")}
            </Button>
          ) : null}
        </div>
      ) : null}

      {state.noImprovements ? (
        <p className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-950">
          {t("story.ai.textEnhancement.noImprovements")}
        </p>
      ) : null}

      {state.diff ? <DiffText segments={state.diff} /> : null}

      {state.diff ? (
        <div className="flex flex-wrap justify-end gap-2">
          <Button onClick={onDismiss} type="button" variant="outline">
            {t("story.ai.textEnhancement.dismiss")}
          </Button>
          <Button disabled={!canApply} onClick={onApply} type="button">
            <Check className="h-4 w-4" />
            {t("story.ai.polish.applyAll")}
          </Button>
        </div>
      ) : null}
    </section>
  );
}
